import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import * as Action from "../../../store/redux/client_reducer.js";
import { serverHost } from "../../../http_common.js";
import BAR_QR_CODE_SCANNER from "../clients-account-comps/point_of_sales_terminal/ware_house_comps/BAR_QR_CODE_SCANNER.jsx";

import Add from "@mui/icons-material/Add";
import ArrowDownward from "@mui/icons-material/ArrowDownward";
import ArrowForwardIos from "@mui/icons-material/ArrowForwardIos";
import ArrowUpward from "@mui/icons-material/ArrowUpward";
import Assessment from "@mui/icons-material/Assessment";
import Autorenew from "@mui/icons-material/Autorenew"; 
import Category from "@mui/icons-material/Category";
import CheckCircle from "@mui/icons-material/CheckCircle";
import Close from "@mui/icons-material/Close";
import CloudDownload from "@mui/icons-material/CloudDownload";
import DeleteOutline from "@mui/icons-material/DeleteOutline";
import EditOutlined from "@mui/icons-material/EditOutlined";
import ExpandMore from "@mui/icons-material/ExpandMore";
import FilterAltOutlined from "@mui/icons-material/FilterAltOutlined";
import History from "@mui/icons-material/History";
import Inventory2Outlined from "@mui/icons-material/Inventory2Outlined";
import LocalShippingOutlined from "@mui/icons-material/LocalShippingOutlined";
import MoreHoriz from "@mui/icons-material/MoreHoriz";
import NotificationsActiveOutlined from "@mui/icons-material/NotificationsActiveOutlined";
import OpenInNew from "@mui/icons-material/OpenInNew";
import QrCode2 from "@mui/icons-material/QrCode2";
import Refresh from "@mui/icons-material/Refresh";
import RemoveCircleOutline from "@mui/icons-material/RemoveCircleOutline";
import Search from "@mui/icons-material/Search";
import SettingsOutlined from "@mui/icons-material/SettingsOutlined";
import ShoppingCartOutlined from "@mui/icons-material/ShoppingCartOutlined";
import SwapHoriz from "@mui/icons-material/SwapHoriz";
import TrendingDown from "@mui/icons-material/TrendingDown";
import TrendingUp from "@mui/icons-material/TrendingUp";
import WarningAmberOutlined from "@mui/icons-material/WarningAmberOutlined";
import WarehouseOutlined from "@mui/icons-material/WarehouseOutlined";
import ViewList from "@mui/icons-material/ViewList";
import ViewModule from "@mui/icons-material/ViewModule";
import IsLoading from "../../../IsLoading";
import AlertBar from "../../../AlertBar";

import "./inventoryPage.css";

const STOCK_LIMITS = {
  OUT: 0,
  VERY_LOW: 5,
  LOW: 15,
};

const PAGE_SIZE = 12;

const money = (value) =>
  `₦${Number(value || 0).toLocaleString("en-NG", {
    minimumFractionDigits: 0,
    maximumFractionDigits: 2,
  })}`;

const number = (value) =>
  Number(value || 0).toLocaleString("en-NG", {
    maximumFractionDigits: 2,
  });

const getProductQuantity = (product) => {
  if (!product?.inventories?.length) return 0;

  return product.inventories.reduce(
    (sum, inventory) => sum + Number(inventory?.stock?.quantity ?? inventory?.quantity ?? 0),
    0,
  );
};

const getProductCost = (product) =>
  Number(
    product?.pricing?.costPrice ??
      product?.costPrice ??
      product?.purchasePrice ??
      0,
  );

const getProductSellingPrice = (product) =>
  Number(
    product?.pricing?.sellingPrice ??
      product?.sellingPrice ??
      product?.price ??
      0,
  );

const getProductSKU = (product) =>
  product?.sku ||
  product?.SKU ||
  product?.productSKU ||
  product?.barcode ||
  "N/A";

const getProductCategory = (product) =>
  product?.category?.name ||
  product?.categoryName ||
  product?.category ||
  "Uncategorized";

const getProductBrand = (product) =>
  product?.brand?.name ||
  product?.brand ||
  "No brand";

const getStockStatus = (quantity) => {
  if (quantity <= STOCK_LIMITS.OUT) {
    return {
      key: "out",
      label: "Out of stock",
      className: "inventoryStatusOut",
    };
  }

  if (quantity <= STOCK_LIMITS.VERY_LOW) {
    return {
      key: "very-low",
      label: "Very low",
      className: "inventoryStatusVeryLow",
    };
  }

  if (quantity <= STOCK_LIMITS.LOW) {
    return {
      key: "low",
      label: "Low stock",
      className: "inventoryStatusLow",
    };
  }

  return {
    key: "healthy",
    label: "Healthy",
    className: "inventoryStatusHealthy",
  };
};

const getProductImage = (product) =>
  product?.images?.[0]?.url ||
  product?.images?.[0] ||
  product?.image ||
  product?.photo ||
  product?.files?.productImage ||
  "";

export default function InventoryPage() {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  function handleSubNavigator(hook, title, main, item) {
    dispatch(Action.dispatchDashboardSubNavigator({ hook, title, main }));
    const query = item?.sku ? `?sku=${encodeURIComponent(item.sku)}` : "";
    navigate(`/advance-companies-management-system/company-account${query}`);
  }

  const companyData = useSelector(
    (state) => state.clientFunction?.companiesDB?.clientData,
  );

  const { accessType } = useSelector(
    (state) => state.clientFunction || {},
  );

  const [loading, setLoading] = useState(false);
  const [alert, setAlert] = useState(null);

  const [products, setProducts] = useState([]);

  const [searchValue, setSearchValue] = useState("");
  const [selectedCategory, setSelectedCategory] = useState("ALL");
  const [selectedBrand, setSelectedBrand] = useState("ALL");
  const [selectedStatus, setSelectedStatus] = useState("ALL");
  const [selectedWarehouse, setSelectedWarehouse] = useState("ALL");

  const [sortBy, setSortBy] = useState("updated");
  const [sortDirection, setSortDirection] = useState("desc");

  const [viewMode, setViewMode] = useState("table");

  const [selectedProducts, setSelectedProducts] = useState([]);

  const [activeProduct, setActiveProduct] = useState(null);
  const [showProductDrawer, setShowProductDrawer] = useState(false);
  const [showScanner, setShowScanner] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [showAnalytics, setShowAnalytics] = useState(true);

  const [showStockModal, setShowStockModal] = useState(false);
  const [stockAction, setStockAction] = useState("adjust");
  const [stockQuantity, setStockQuantity] = useState("");
  const [stockReason, setStockReason] = useState("");

  const [showBulkMenu, setShowBulkMenu] = useState(false);

  const [page, setPage] = useState(1);

  const [visibleColumns, setVisibleColumns] = useState({
    sku: true,
    category: true,
    quantity: true,
    cost: true,
    selling: true,
    value: true,
    status: true,
    updated: true,
  });

  const apiGetInventory = async () => {
    setLoading(true);

    try {
      const BASE_URL = serverHost;
      const clientId = companyData?.clientId;

      if (!clientId) {
        throw new Error(
          "Unable to load inventory. Missing client information.",
        );
      }

      /*
       * Keep your existing backend endpoint here if it already exists.
       *
       * The component accepts:
       *
       * response.products
       * response.inventory
       * response.inventoryData
       *
       * so your existing API response can be mapped without changing
       * the inventory UI.
       */

      const response = await axios.get(
        `${BASE_URL}/manage_products/client/${clientId}/items-data/api-get-company-items`,
      );

      const payload =
        response?.data?.productDB ||
        response?.data?.products ||
        response?.data?.inventory ||
        response?.data?.inventoryData ||
        response?.data?.data ||
        [];

      setProducts(Array.isArray(payload) ? payload : []);

      if (response?.data?.status && ![200, 201].includes(response.data.status)) {
        setAlert({
          message:
            response?.data?.message ||
            "Inventory could not be loaded.",
          type: "error",
        });
      }
    } catch (error) {
      console.error("Inventory Error:", error?.code || "REQUEST_FAILED");

      setAlert({
        message:
          error?.response?.data?.message ||
          error?.message ||
          "Unable to load inventory.",
        type: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    apiGetInventory();
  }, [companyData?.clientId, accessType]);

  const inventoryProducts = useMemo(() => {
    return products.map((product) => {
      const quantity = getProductQuantity(product);
      const costPrice = getProductCost(product);
      const sellingPrice = getProductSellingPrice(product);

      return {
        ...product,
        __quantity: quantity,
        __costPrice: costPrice,
        __sellingPrice: sellingPrice,
        __costValue: quantity * costPrice,
        __sellingValue: quantity * sellingPrice,
        __profitValue: quantity * (sellingPrice - costPrice),
        __sku: getProductSKU(product),
        __category: getProductCategory(product),
        __brand: getProductBrand(product),
        __image: getProductImage(product),
        __status: getStockStatus(quantity),
      };
    });
  }, [products]);

  const categories = useMemo(() => {
    return [
      "ALL",
      ...new Set(
        inventoryProducts.map((product) => product.__category),
      ),
    ];
  }, [inventoryProducts]);

  const brands = useMemo(() => {
    return [
      "ALL",
      ...new Set(
        inventoryProducts.map((product) => product.__brand),
      ),
    ];
  }, [inventoryProducts]);

  const warehouses = useMemo(() => {
    const values = [];

    inventoryProducts.forEach((product) => {
      product?.inventories?.forEach((inventory) => {
        const warehouse =
          inventory?.warehouseName ||
          inventory?.warehouse?.name ||
          inventory?.warehouseId;

        if (warehouse) values.push(warehouse);
      });
    });

    return ["ALL", ...new Set(values)];
  }, [inventoryProducts]);

  const analytics = useMemo(() => {
    const result = inventoryProducts.reduce(
      (acc, product) => {
        acc.products += 1;
        acc.quantity += product.__quantity;
        acc.costValue += product.__costValue;
        acc.sellingValue += product.__sellingValue;
        acc.profit += product.__profitValue;

        if (product.__status.key === "out") {
          acc.outOfStock += 1;
        }

        if (product.__status.key === "very-low") {
          acc.veryLow += 1;
        }

        if (product.__status.key === "low") {
          acc.low += 1;
        }

        if (product.__status.key === "healthy") {
          acc.healthy += 1;
        }

        return acc;
      },
      {
        products: 0,
        quantity: 0,
        costValue: 0,
        sellingValue: 0,
        profit: 0,
        outOfStock: 0,
        veryLow: 0,
        low: 0,
        healthy: 0,
      },
    );

    result.margin =
      result.sellingValue > 0
        ? (result.profit / result.sellingValue) * 100
        : 0;

    result.stockHealth =
      result.products > 0
        ? ((result.healthy / result.products) * 100)
        : 0;

    return result;
  }, [inventoryProducts]);

  const filteredProducts = useMemo(() => {
    const query = searchValue.trim().toLowerCase();

    let result = inventoryProducts.filter((product) => {
      const matchesSearch =
        !query ||
        product?.name?.toLowerCase()?.includes(query) ||
        product.__sku?.toLowerCase()?.includes(query) ||
        product.__category?.toLowerCase()?.includes(query) ||
        product.__brand?.toLowerCase()?.includes(query) ||
        product?.barcode?.toLowerCase?.()?.includes(query);

      const matchesCategory =
        selectedCategory === "ALL" ||
        product.__category === selectedCategory;

      const matchesBrand =
        selectedBrand === "ALL" ||
        product.__brand === selectedBrand;

      const matchesStatus =
        selectedStatus === "ALL" ||
        product.__status.key === selectedStatus;

      const matchesWarehouse =
        selectedWarehouse === "ALL" ||
        product?.inventories?.some(
          (inventory) =>
            inventory?.warehouseName === selectedWarehouse ||
            inventory?.warehouse?.name === selectedWarehouse ||
            inventory?.warehouseId === selectedWarehouse,
        );

      return (
        matchesSearch &&
        matchesCategory &&
        matchesBrand &&
        matchesStatus &&
        matchesWarehouse
      );
    });

    result.sort((a, b) => {
      let first;
      let second;

      switch (sortBy) {
        case "name":
          first = a?.name || "";
          second = b?.name || "";
          break;

        case "quantity":
          first = a.__quantity;
          second = b.__quantity;
          break;

        case "cost":
          first = a.__costValue;
          second = b.__costValue;
          break;

        case "selling":
          first = a.__sellingValue;
          second = b.__sellingValue;
          break;

        case "status":
          first = a.__status.key;
          second = b.__status.key;
          break;

        default:
          first =
            new Date(a?.updatedAt || a?.createdAt || 0).getTime();
          second =
            new Date(b?.updatedAt || b?.createdAt || 0).getTime();
      }

      if (typeof first === "string") {
        return sortDirection === "asc"
          ? first.localeCompare(second)
          : second.localeCompare(first);
      }

      return sortDirection === "asc"
        ? first - second
        : second - first;
    });

    return result;
  }, [
    inventoryProducts,
    searchValue,
    selectedCategory,
    selectedBrand,
    selectedStatus,
    selectedWarehouse,
    sortBy,
    sortDirection,
  ]);

  const totalPages = Math.max(
    1,
    Math.ceil(filteredProducts.length / PAGE_SIZE),
  );

  const paginatedProducts = filteredProducts.slice(
    (page - 1) * PAGE_SIZE,
    page * PAGE_SIZE,
  );

  useEffect(() => {
    setPage(1);
  }, [
    searchValue,
    selectedCategory,
    selectedBrand,
    selectedStatus,
    selectedWarehouse,
  ]);

  const allVisibleSelected =
    paginatedProducts.length > 0 &&
    paginatedProducts.every((product) =>
      selectedProducts.includes(product._id),
    );

  const toggleProductSelection = (id) => {
    setSelectedProducts((current) =>
      current.includes(id)
        ? current.filter((item) => item !== id)
        : [...current, id],
    );
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedProducts((current) =>
        current.filter(
          (id) =>
            !paginatedProducts.some(
              (product) => product._id === id,
            ),
        ),
      );
    } else {
      const visibleIds = paginatedProducts.map(
        (product) => product._id,
      );

      setSelectedProducts((current) => [
        ...new Set([...current, ...visibleIds]),
      ]);
    }
  };

  const openProduct = (product) => {
    setActiveProduct(product);
    setShowProductDrawer(true);
  };

  const changeSort = (value) => {
    if (sortBy === value) {
      setSortDirection((current) =>
        current === "asc" ? "desc" : "asc",
      );
    } else {
      setSortBy(value);
      setSortDirection("asc");
    }
  };

  const clearFilters = () => {
    setSearchValue("");
    setSelectedCategory("ALL");
    setSelectedBrand("ALL");
    setSelectedStatus("ALL");
    setSelectedWarehouse("ALL");
  };

  const executeStockAction = () => {
    handleSubNavigator(
      stockAction === "add" ? "recieves" : "adjusment",
      stockAction === "add" ? "Recieves" : "Adjustment",
      stockAction === "add" ? "Purchases" : "Manage Stock",
    );
  };

  const exportInventory = () => {
    const headers = [
      "Product",
      "SKU",
      "Category",
      "Brand",
      "Quantity",
      "Cost Price",
      "Selling Price",
      "Inventory Cost Value",
      "Inventory Selling Value",
      "Status",
    ];

    const rows = filteredProducts.map((product) => [
      product?.name || "",
      product.__sku,
      product.__category,
      product.__brand,
      product.__quantity,
      product.__costPrice,
      product.__sellingPrice,
      product.__costValue,
      product.__sellingValue,
      product.__status.label,
    ]);

    const csv = [headers, ...rows]
      .map((row) =>
        row
          .map((value) => `"${String(value).replace(/"/g, '""')}"`)
          .join(","),
      )
      .join("\n");

    const blob = new Blob([csv], {
      type: "text/csv;charset=utf-8;",
    });

    const url = URL.createObjectURL(blob);
    const anchor = document.createElement("a");

    anchor.href = url;
    anchor.download = `universe-inventory-${new Date()
      .toISOString()
      .slice(0, 10)}.csv`;

    anchor.click();

    URL.revokeObjectURL(url);
  };

  return (
    <>
      {loading && <IsLoading />}

      {alert && (
        <AlertBar
          alert={alert}
          setAlert={setAlert}
        />
      )}

      {showScanner && (
        <div id="modalContainer" onClick={() => setShowScanner(false)}>
          <BAR_QR_CODE_SCANNER
            reveal="cam-scanner-open"
            setReveal={() => setShowScanner(false)}
            onScan={(code) => {
              setSearchValue(code);
              setPage(1);
              setShowScanner(false);
            }}
          />
        </div>
      )}

      <section className="inventoryPage fx-cl space3">

        {/* =========================================================
            PAGE HEADER
        ========================================================== */}

        <div className="inventoryPageHeader fx-jb fx-ac space2">

          <div className="fx-cl spacem">
            <div className="fx-ac space1">
              <figure className="inventoryHeaderIcon fx-ac fx-jc">
                <Inventory2Outlined />
              </figure>

              <div className="fx-cl spacem">
                <h1 className="fs6 fw600">
                  Inventory
                </h1>

                <p className="fs2 cb">
                  Manage products, stock levels, warehouses and
                  inventory movements.
                </p>
              </div>
            </div>
          </div>

          <div className="inventoryHeaderActions fx-ac space1">

            <button
              className="inventorySecondaryBtn fx-ac space"
              onClick={apiGetInventory}
            >
              <Refresh />
              <span>Refresh</span>
            </button>

            <button
              className="inventorySecondaryBtn fx-ac space"
              onClick={exportInventory}
            >
              <CloudDownload />
              <span>Export</span>
            </button>

            <button
              className="inventoryPrimaryBtn fx-ac space"
              onClick={() => handleSubNavigator("add-new-item", "Add New Item", "Manage Items")}
            >
              <Add />
              <span>Add Items</span>
            </button>

          </div>
        </div>

        {/* =========================================================
            COMMAND BAR
        ========================================================== */}

        <div className="inventoryCommandBar shadowA fx-ac space1">

          <div className="inventorySearchBox fx-ac space1">
            <Search />

            <input
              value={searchValue}
              onChange={(event) =>
                setSearchValue(event.target.value)
              }
              placeholder="Search products, SKU, barcode, category..."
            />

            {searchValue && (
              <button
                className="inventorySearchClear"
                onClick={() => setSearchValue("")}
              >
                <Close />
              </button>
            )}
          </div>

          <button
            className={`inventoryFilterBtn fx-ac space ${
              showFilters ? "inventoryFilterActive" : ""
            }`}
            onClick={() => setShowFilters((current) => !current)}
          >
            <FilterAltOutlined />
            <span>Filters</span>

            {(selectedCategory !== "ALL" ||
              selectedBrand !== "ALL" ||
              selectedStatus !== "ALL" ||
              selectedWarehouse !== "ALL") && (
              <span className="inventoryFilterCount">
                {[
                  selectedCategory !== "ALL",
                  selectedBrand !== "ALL",
                  selectedStatus !== "ALL",
                  selectedWarehouse !== "ALL",
                ].filter(Boolean).length}
              </span>
            )}
          </button>

          <div className="inventorySortBox fx-ac space">
            <span className="fs2 cb">
              Sort:
            </span>

            <select
              value={sortBy}
              onChange={(event) =>
                changeSort(event.target.value)
              }
            >
              <option value="updated">
                Recently updated
              </option>

              <option value="name">
                Product name
              </option>

              <option value="quantity">
                Quantity
              </option>

              <option value="cost">
                Inventory cost
              </option>

              <option value="selling">
                Inventory value
              </option>

              <option value="status">
                Stock status
              </option>
            </select>

            {sortDirection === "asc" ? (
              <ArrowUpward />
            ) : (
              <ArrowDownward />
            )}
          </div>

          <div className="inventoryViewSwitch fx-ac">
            <button
              className={
                viewMode === "table"
                  ? "inventoryViewActive"
                  : ""
              }
              onClick={() => setViewMode("table")}
            >
              <ViewList />
            </button>

            <button
              className={
                viewMode === "grid"
                  ? "inventoryViewActive"
                  : ""
              }
              onClick={() => setViewMode("grid")}
            >
              <ViewModule />
            </button>
          </div>

        </div>

        {/* =========================================================
            ADVANCED FILTER PANEL
        ========================================================== */}

        {showFilters && (
          <div className="inventoryFilterPanel shadowA fx-cl space2">

            <div className="inventoryFilterHeader fx-jb fx-ac">
              <div className="fx-cl spacem">
                <h3 className="fs4 fw600">
                  Advanced filters
                </h3>

                <p className="fs2 cb">
                  Narrow down your inventory to exactly what
                  you need.
                </p>
              </div>

              <button
                className="inventoryClearFilterBtn"
                onClick={clearFilters}
              >
                Clear filters
              </button>
            </div>

            <div className="g g4 inventoryFilterGrid">

              <label className="fx-cl space">
                <span className="fs2 fw500">
                  Category
                </span>

                <select
                  value={selectedCategory}
                  onChange={(event) =>
                    setSelectedCategory(event.target.value)
                  }
                >
                  {categories.map((category) => (
                    <option
                      key={category}
                      value={category}
                    >
                      {category === "ALL"
                        ? "All categories"
                        : category}
                    </option>
                  ))}
                </select>
              </label>

              <label className="fx-cl space">
                <span className="fs2 fw500">
                  Brand
                </span>

                <select
                  value={selectedBrand}
                  onChange={(event) =>
                    setSelectedBrand(event.target.value)
                  }
                >
                  {brands.map((brand) => (
                    <option
                      key={brand}
                      value={brand}
                    >
                      {brand === "ALL"
                        ? "All brands"
                        : brand}
                    </option>
                  ))}
                </select>
              </label>

              <label className="fx-cl space">
                <span className="fs2 fw500">
                  Stock status
                </span>

                <select
                  value={selectedStatus}
                  onChange={(event) =>
                    setSelectedStatus(event.target.value)
                  }
                >
                  <option value="ALL">
                    All stock levels
                  </option>

                  <option value="healthy">
                    Healthy
                  </option>

                  <option value="low">
                    Low stock
                  </option>

                  <option value="very-low">
                    Very low
                  </option>

                  <option value="out">
                    Out of stock
                  </option>
                </select>
              </label>

              <label className="fx-cl space">
                <span className="fs2 fw500">
                  Warehouse
                </span>

                <select
                  value={selectedWarehouse}
                  onChange={(event) =>
                    setSelectedWarehouse(event.target.value)
                  }
                >
                  {warehouses.map((warehouse) => (
                    <option
                      key={warehouse}
                      value={warehouse}
                    >
                      {warehouse === "ALL"
                        ? "All warehouses"
                        : warehouse}
                    </option>
                  ))}
                </select>
              </label>

            </div>
          </div>
        )}

        {/* =========================================================
            KPI CARDS
        ========================================================== */}

        <div className="inventoryKpiGrid">

          <div className="inventoryKpiCard shadowA fx-cl space1">
            <div className="fx-jb fx-ac">
              <div className="inventoryKpiIcon blue fx-ac fx-jc">
                <Inventory2Outlined />
              </div>

              <span className="inventoryKpiTrend positive">
                <TrendingUp />
                Live
              </span>
            </div>

            <div className="fx-cl spacem">
              <span className="fs2 cb">
                Total products
              </span>

              <strong className="fs7">
                {number(analytics.products)}
              </strong>

              <span className="fs2 cb">
                {number(analytics.quantity)} total units
              </span>
            </div>
          </div>

          <div className="inventoryKpiCard shadowA fx-cl space1">
            <div className="fx-jb fx-ac">
              <div className="inventoryKpiIcon purple fx-ac fx-jc">
                <Assessment />
              </div>

              <span className="fs2 cb">
                At cost
              </span>
            </div>

            <div className="fx-cl spacem">
              <span className="fs2 cb">
                Inventory cost
              </span>

              <strong className="fs6">
                {money(analytics.costValue)}
              </strong>

              <span className="fs2 cb">
                Current acquisition value
              </span>
            </div>
          </div>

          <div className="inventoryKpiCard shadowA fx-cl space1">
            <div className="fx-jb fx-ac">
              <div className="inventoryKpiIcon green fx-ac fx-jc">
                <TrendingUp />
              </div>

              <span className="inventoryKpiTrend positive">
                Value
              </span>
            </div>

            <div className="fx-cl spacem">
              <span className="fs2 cb">
                Potential sales value
              </span>

              <strong className="fs6">
                {money(analytics.sellingValue)}
              </strong>

              <span className="fs2 cb">
                {analytics.margin.toFixed(1)}% estimated margin
              </span>
            </div>
          </div>

          <div className="inventoryKpiCard shadowA fx-cl space1">
            <div className="fx-jb fx-ac">
              <div className="inventoryKpiIcon orange fx-ac fx-jc">
                <WarningAmberOutlined />
              </div>

              <span className="inventoryKpiTrend warning">
                Attention
              </span>
            </div>

            <div className="fx-cl spacem">
              <span className="fs2 cb">
                Stock requiring attention
              </span>

              <strong className="fs7">
                {number(
                  analytics.outOfStock +
                    analytics.veryLow +
                    analytics.low,
                )}
              </strong>

              <span className="fs2 cb">
                {analytics.outOfStock} out ·{" "}
                {analytics.veryLow} very low ·{" "}
                {analytics.low} low
              </span>
            </div>
          </div>

        </div>

        {/* =========================================================
            INVENTORY HEALTH
        ========================================================== */}

        {showAnalytics && (
          <div className="inventoryHealthSection shadowA fx-cl space2">

            <div className="fx-jb fx-ac">
              <div className="fx-ac space1">
                <figure className="inventorySectionIcon fx-ac fx-jc">
                  <Assessment />
                </figure>

                <div className="fx-cl spacem">
                  <h3 className="fs4 fw600">
                    Inventory health
                  </h3>

                  <p className="fs2 cb">
                    Real-time overview of your stock condition.
                  </p>
                </div>
              </div>

              <button
                className="inventoryCollapseBtn"
                onClick={() =>
                  setShowAnalytics((current) => !current)
                }
              >
                <ExpandMore />
              </button>
            </div>

            <div className="inventoryHealthBody">

              <div className="inventoryHealthScore fx-cl fx-ac fx-jc">
                <div
                  className="inventoryHealthCircle"
                  style={{
                    "--health-progress": `${analytics.stockHealth}%`,
                  }}
                >
                  <div className="inventoryHealthCircleInner fx-cl fx-ac fx-jc">
                    <strong className="fs7">
                      {Math.round(analytics.stockHealth)}%
                    </strong>

                    <span className="fs1">
                      Healthy
                    </span>
                  </div>
                </div>

                <p className="fs2 cb">
                  Overall stock health
                </p>
              </div>

              <div className="inventoryHealthStats">

                <div className="inventoryHealthStat fx-ac space1">
                  <span className="inventoryHealthDot healthy" />

                  <div className="fx-cl spacem">
                    <span className="fs2">
                      Healthy stock
                    </span>

                    <strong className="fs4">
                      {number(analytics.healthy)}
                    </strong>
                  </div>

                  <span className="fs2 cb">
                    products
                  </span>
                </div>

                <div className="inventoryHealthStat fx-ac space1">
                  <span className="inventoryHealthDot low" />

                  <div className="fx-cl spacem">
                    <span className="fs2">
                      Low stock
                    </span>

                    <strong className="fs4">
                      {number(analytics.low)}
                    </strong>
                  </div>

                  <span className="fs2 cb">
                    products
                  </span>
                </div>

                <div className="inventoryHealthStat fx-ac space1">
                  <span className="inventoryHealthDot veryLow" />

                  <div className="fx-cl spacem">
                    <span className="fs2">
                      Very low
                    </span>

                    <strong className="fs4">
                      {number(analytics.veryLow)}
                    </strong>
                  </div>

                  <span className="fs2 cb">
                    products
                  </span>
                </div>

                <div className="inventoryHealthStat fx-ac space1">
                  <span className="inventoryHealthDot out" />

                  <div className="fx-cl spacem">
                    <span className="fs2">
                      Out of stock
                    </span>

                    <strong className="fs4">
                      {number(analytics.outOfStock)}
                    </strong>
                  </div>

                  <span className="fs2 cb">
                    products
                  </span>
                </div>

              </div>

              <div className="inventoryQuickActions fx-cl space1">

                <button
                  className="inventoryQuickAction fx-ac space"
                  onClick={() =>
                    setSelectedStatus("out")
                  }
                >
                  <RemoveCircleOutline />
                  <span>View out of stock</span>
                  <ArrowForwardIos />
                </button>

                <button
                  className="inventoryQuickAction fx-ac space"
                  onClick={() =>
                    setSelectedStatus("low")
                  }
                >
                  <WarningAmberOutlined />
                  <span>View low stock</span>
                  <ArrowForwardIos />
                </button>

                <button
                  className="inventoryQuickAction fx-ac space"
                  onClick={() =>
                    setAlert({ type: "info", message: "Movement history is not connected yet. Existing stock records are available under Manage Stock." })
                  }
                >
                  <History />
                  <span>Inventory movements</span>
                  <ArrowForwardIos />
                </button>

              </div>

            </div>
          </div>
        )}

        {/* =========================================================
            BULK ACTION BAR
        ========================================================== */}

        {selectedProducts.length > 0 && (
          <div className="inventoryBulkBar shadowA fx-jb fx-ac">

            <div className="fx-ac space1">
              <span className="inventorySelectedCount">
                {selectedProducts.length}
              </span>

              <span className="fs3 fw500">
                products selected
              </span>

              <button
                className="inventoryTextBtn"
                onClick={() => setSelectedProducts([])}
              >
                Clear
              </button>
            </div>

            <div className="fx-ac space1">

              <button
                className="inventoryBulkAction fx-ac space"
                onClick={() => handleSubNavigator("adjusment", "Adjustment", "Manage Stock")}
              >
                <Assessment />
                Adjust stock
              </button>

              <button
                className="inventoryBulkAction fx-ac space"
                onClick={() =>
                  handleSubNavigator("transfers", "Transfers", "Manage Stock")
                }
              >
                <SwapHoriz />
                Transfer
              </button>

              <button
                className="inventoryBulkAction fx-ac space"
                onClick={exportInventory}
              >
                <CloudDownload />
                Export
              </button>

            </div>
          </div>
        )}

        {/* =========================================================
            TABLE
        ========================================================== */}

        {viewMode === "table" && (
          <div className="inventoryTableCard shadowA">

            <div className="inventoryTableHeader fx-jb fx-ac">

              <div className="fx-cl spacem">
                <h3 className="fs4 fw600">
                  Inventory items
                </h3>

                <p className="fs2 cb">
                  Showing {number(paginatedProducts.length)} of{" "}
                  {number(filteredProducts.length)} products
                </p>
              </div>

              <div className="inventoryTableHeaderActions fx-ac space1">

                <div className="inventoryColumnMenu relative">
                  <button
                    className="inventoryIconBtn"
                    title="Column settings"
                  >
                    <SettingsOutlined />
                  </button>

                  <div className="inventoryColumnDropdown">
                    <strong className="fs2">
                      Columns
                    </strong>

                    {Object.keys(visibleColumns).map(
                      (column) => (
                        <label
                          key={column}
                          className="fx-ac space"
                        >
                          <input
                            type="checkbox"
                            checked={
                              visibleColumns[column]
                            }
                            onChange={() =>
                              setVisibleColumns(
                                (current) => ({
                                  ...current,
                                  [column]:
                                    !current[column],
                                }),
                              )
                            }
                          />

                          <span>
                            {column
                              .charAt(0)
                              .toUpperCase() +
                              column.slice(1)}
                          </span>
                        </label>
                      ),
                    )}
                  </div>
                </div>

                <button
                  className="inventoryIconBtn"
                  title="Barcode scanner"
                  onClick={() =>
                    setShowScanner(true)
                  }
                >
                  <CheckCircle />
                </button>

              </div>
            </div>

            <div className="inventoryTableScroller">

              <table className="inventoryTable">

                <thead>
                  <tr>

                    <th className="inventoryCheckboxCell">
                      <input
                        type="checkbox"
                        checked={allVisibleSelected}
                        onChange={toggleSelectAll}
                      />
                    </th>

                    <th>Product</th>

                    {visibleColumns.sku && (
                      <th>SKU / Barcode</th>
                    )}

                    {visibleColumns.category && (
                      <th>Category</th>
                    )}

                    {visibleColumns.quantity && (
                      <th>Stock</th>
                    )}

                    {visibleColumns.cost && (
                      <th>Cost</th>
                    )}

                    {visibleColumns.selling && (
                      <th>Selling</th>
                    )}

                    {visibleColumns.value && (
                      <th>Inventory value</th>
                    )}

                    {visibleColumns.status && (
                      <th>Status</th>
                    )}

                    {visibleColumns.updated && (
                      <th>Updated</th>
                    )}

                    <th />
                  </tr>
                </thead>

                <tbody>

                  {paginatedProducts.map((product) => {

                    const isSelected =
                      selectedProducts.includes(
                        product._id,
                      );

                    return (
                      <tr
                        key={product._id}
                        className={
                          isSelected
                            ? "inventoryRowSelected"
                            : ""
                        }
                      >

                        <td className="inventoryCheckboxCell">
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() =>
                              toggleProductSelection(
                                product._id,
                              )
                            }
                          />
                        </td>

                        <td>
                          <div
                            className="inventoryProductCell fx-ac space1 cp"
                            onClick={() =>
                              openProduct(product)
                            }
                          >

                            {product.__image ? (
                              <img
                                src={product.__image}
                                alt=""
                                className="inventoryProductImage"
                              />
                            ) : (
                              <figure className="inventoryProductImage inventoryImagePlaceholder fx-ac fx-jc">
                                <Inventory2Outlined />
                              </figure>
                            )}

                            <div className="fx-cl spacem">

                              <strong className="fs3 fw500">
                                {product?.name ||
                                  "Unnamed product"}
                              </strong>

                              <span className="fs1 cb">
                                {product.__brand}
                              </span>

                            </div>

                          </div>
                        </td>

                        {visibleColumns.sku && (
                          <td>
                            <div className="fx-cl spacem">
                              <span className="fs2 fw500">
                                {product.__sku}
                              </span>

                              {product?.barcode && (
                                <span className="fs1 cb">
                                  {product.barcode}
                                </span>
                              )}
                            </div>
                          </td>
                        )}

                        {visibleColumns.category && (
                          <td>
                            <span className="inventoryCategoryTag">
                              {product.__category}
                            </span>
                          </td>
                        )}

                        {visibleColumns.quantity && (
                          <td>
                            <div className="fx-cl spacem">
                              <strong className="fs3">
                                {number(
                                  product.__quantity,
                                )}
                              </strong>

                              <span className="fs1 cb">
                                units
                              </span>
                            </div>
                          </td>
                        )}

                        {visibleColumns.cost && (
                          <td>
                            <span className="fs2">
                              {money(
                                product.__costPrice,
                              )}
                            </span>
                          </td>
                        )}

                        {visibleColumns.selling && (
                          <td>
                            <span className="fs2 fw500">
                              {money(
                                product.__sellingPrice,
                              )}
                            </span>
                          </td>
                        )}

                        {visibleColumns.value && (
                          <td>
                            <div className="fx-cl spacem">
                              <strong className="fs2">
                                {money(
                                  product.__sellingValue,
                                )}
                              </strong>

                              <span className="fs1 cb">
                                Cost:{" "}
                                {money(
                                  product.__costValue,
                                )}
                              </span>
                            </div>
                          </td>
                        )}

                        {visibleColumns.status && (
                          <td>
                            <span
                              className={`inventoryStatusBadge ${product.__status.className}`}
                            >
                              <span />
                              {product.__status.label}
                            </span>
                          </td>
                        )}

                        {visibleColumns.updated && (
                          <td>
                            <span className="fs1 cb">
                              {product?.updatedAt
                                ? new Date(
                                    product.updatedAt,
                                  ).toLocaleDateString(
                                    "en-GB",
                                  )
                                : "—"}
                            </span>
                          </td>
                        )}

                        <td>
                          <button
                            className="inventoryMoreBtn"
                            onClick={() =>
                              openProduct(product)
                            }
                          >
                            <MoreHoriz />
                          </button>
                        </td>

                      </tr>
                    );
                  })}

                </tbody>
              </table>

              {paginatedProducts.length === 0 && (
                <div className="inventoryEmptyState fx-cl fx-ac fx-jc space2">

                  <figure className="inventoryEmptyIcon fx-ac fx-jc">
                    <Inventory2Outlined />
                  </figure>

                  <div className="fx-cl fx-ac spacem">
                    <h3 className="fs5 fw600">
                      No inventory found
                    </h3>

                    <p className="fs2 cb">
                      Try changing your filters or search
                      terms.
                    </p>
                  </div>

                  <button
                    className="inventorySecondaryBtn"
                    onClick={clearFilters}
                  >
                    Clear filters
                  </button>

                </div>
              )}

            </div>

            {/* PAGINATION */}

            {filteredProducts.length > 0 && (
              <div className="inventoryPagination fx-jb fx-ac">

                <span className="fs2 cb">
                  Page {page} of {totalPages}
                </span>

                <div className="fx-ac space1">

                  <button
                    className="inventoryPageBtn"
                    disabled={page <= 1}
                    onClick={() =>
                      setPage((current) =>
                        Math.max(1, current - 1),
                      )
                    }
                  >
                    Previous
                  </button>

                  {Array.from(
                    {
                      length: Math.min(totalPages, 5),
                    },
                    (_, index) => {
                      const pageNumber =
                        index + 1;

                      return (
                        <button
                          key={pageNumber}
                          className={`inventoryPageNumber ${
                            page === pageNumber
                              ? "inventoryPageNumberActive"
                              : ""
                          }`}
                          onClick={() =>
                            setPage(pageNumber)
                          }
                        >
                          {pageNumber}
                        </button>
                      );
                    },
                  )}

                  <button
                    className="inventoryPageBtn"
                    disabled={page >= totalPages}
                    onClick={() =>
                      setPage((current) =>
                        Math.min(
                          totalPages,
                          current + 1,
                        ),
                      )
                    }
                  >
                    Next
                  </button>

                </div>
              </div>
            )}

          </div>
        )}

        {/* =========================================================
            GRID VIEW
        ========================================================== */}

        {viewMode === "grid" && (
          <div className="inventoryGridView">

            {paginatedProducts.map((product) => (
              <article
                key={product._id}
                className="inventoryProductCard shadowA fx-cl"
              >

                <div className="inventoryGridImageWrap relative">

                  {product.__image ? (
                    <img
                      src={product.__image}
                      alt=""
                      className="inventoryGridImage"
                    />
                  ) : (
                    <div className="inventoryGridImage inventoryImagePlaceholder fx-ac fx-jc">
                      <Inventory2Outlined />
                    </div>
                  )}

                  <span
                    className={`inventoryStatusBadge inventoryGridStatus ${product.__status.className}`}
                  >
                    <span />
                    {product.__status.label}
                  </span>

                  <button
                    className="inventoryGridMore"
                    onClick={() =>
                      openProduct(product)
                    }
                  >
                    <MoreHoriz />
                  </button>

                </div>

                <div className="inventoryGridBody fx-cl space1">

                  <div className="fx-jb fx-ac">
                    <div className="fx-cl spacem">
                      <h3 className="fs4 fw500">
                        {product?.name ||
                          "Unnamed product"}
                      </h3>

                      <span className="fs1 cb">
                        {product.__brand}
                      </span>
                    </div>

                    <span className="inventoryCategoryTag">
                      {product.__category}
                    </span>
                  </div>

                  <div className="inventoryGridStats g g2">

                    <div className="fx-cl spacem">
                      <span className="fs1 cb">
                        Available
                      </span>

                      <strong className="fs5">
                        {number(product.__quantity)}
                      </strong>
                    </div>

                    <div className="fx-cl spacem">
                      <span className="fs1 cb">
                        Selling price
                      </span>

                      <strong className="fs4">
                        {money(
                          product.__sellingPrice,
                        )}
                      </strong>
                    </div>

                  </div>

                  <div className="inventoryGridFooter fx-jb fx-ac">

                    <span className="fs1 cb">
                      SKU: {product.__sku}
                    </span>

                    <button
                      className="inventoryOpenProduct fx-ac space"
                      onClick={() =>
                        openProduct(product)
                      }
                    >
                      View
                      <ArrowForwardIos />
                    </button>

                  </div>

                </div>
              </article>
            ))}

            {paginatedProducts.length === 0 && (
              <div className="inventoryEmptyState fx-cl fx-ac fx-jc space2">
                <figure className="inventoryEmptyIcon fx-ac fx-jc">
                  <Inventory2Outlined />
                </figure>

                <h3 className="fs5 fw600">
                  No inventory found
                </h3>

                <p className="fs2 cb">
                  Try changing your search or filters.
                </p>

                <button
                  className="inventorySecondaryBtn"
                  onClick={clearFilters}
                >
                  Clear filters
                </button>
              </div>
            )}

          </div>
        )}

      </section>

      {/* =========================================================
          PRODUCT DRAWER
      ========================================================== */}

      {showProductDrawer && activeProduct && (
        <div
          className="inventoryDrawerOverlay"
          onClick={() =>
            setShowProductDrawer(false)
          }
        >

          <aside
            className="inventoryProductDrawer fx-cl"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            <div className="inventoryDrawerHeader fx-jb fx-ac">

              <div className="fx-cl spacem">
                <span className="fs1 cb">
                  PRODUCT DETAILS
                </span>

                <h2 className="fs5 fw600">
                  {activeProduct?.name ||
                    "Unnamed product"}
                </h2>
              </div>

              <button
                className="inventoryIconBtn"
                onClick={() =>
                  setShowProductDrawer(false)
                }
              >
                <Close />
              </button>

            </div>

            <div className="inventoryDrawerContent fx-cl space2">

              <div className="inventoryDrawerProductHero fx-cl fx-ac space1">

                {activeProduct.__image ? (
                  <img
                    src={activeProduct.__image}
                    alt=""
                    className="inventoryDrawerProductImage"
                  />
                ) : (
                  <figure className="inventoryDrawerProductImage inventoryImagePlaceholder fx-ac fx-jc">
                    <Inventory2Outlined />
                  </figure>
                )}

                <div className="fx-cl fx-ac spacem">
                  <h3 className="fs5 fw500">
                    {activeProduct?.name}
                  </h3>

                  <span className="fs2 cb">
                    SKU: {activeProduct.__sku}
                  </span>
                </div>

              </div>

              <div className="inventoryDrawerStatus fx-ac fx-jb">

                <div className="fx-cl spacem">
                  <span className="fs1 cb">
                    STOCK STATUS
                  </span>

                  <span
                    className={`inventoryStatusBadge ${activeProduct.__status.className}`}
                  >
                    <span />
                    {activeProduct.__status.label}
                  </span>
                </div>

                <div className="fx-cl fx-ae spacem">
                  <span className="fs1 cb">
                    AVAILABLE
                  </span>

                  <strong className="fs6">
                    {number(
                      activeProduct.__quantity,
                    )}
                  </strong>
                </div>

              </div>

              <div className="inventoryDrawerMetricGrid g g2">

                <div className="inventoryDrawerMetric fx-cl spacem">
                  <span className="fs1 cb">
                    Cost price
                  </span>

                  <strong className="fs4">
                    {money(
                      activeProduct.__costPrice,
                    )}
                  </strong>
                </div>

                <div className="inventoryDrawerMetric fx-cl spacem">
                  <span className="fs1 cb">
                    Selling price
                  </span>

                  <strong className="fs4">
                    {money(
                      activeProduct.__sellingPrice,
                    )}
                  </strong>
                </div>

                <div className="inventoryDrawerMetric fx-cl spacem">
                  <span className="fs1 cb">
                    Cost value
                  </span>

                  <strong className="fs4">
                    {money(
                      activeProduct.__costValue,
                    )}
                  </strong>
                </div>

                <div className="inventoryDrawerMetric fx-cl spacem">
                  <span className="fs1 cb">
                    Sales value
                  </span>

                  <strong className="fs4">
                    {money(
                      activeProduct.__sellingValue,
                    )}
                  </strong>
                </div>

              </div>

              <div className="inventoryDrawerSection fx-cl space1">

                <div className="fx-jb fx-ac">
                  <h3 className="fs4 fw500">
                    Inventory locations
                  </h3>

                  <WarehouseOutlined />
                </div>

                {activeProduct?.inventories?.length ? (
                  activeProduct.inventories.map(
                    (inventory, index) => (
                      <div
                        className="inventoryLocationRow fx-jb fx-ac"
                        key={
                          inventory?.inventoryId ||
                          inventory?._id ||
                          index
                        }
                      >

                        <div className="fx-ac space1">

                          <figure className="inventoryLocationIcon fx-ac fx-jc">
                            <WarehouseOutlined />
                          </figure>

                          <div className="fx-cl spacem">
                            <strong className="fs2">
                              {inventory?.warehouseName ||
                                inventory?.warehouse?.name ||
                                "Warehouse"}
                            </strong>

                            <span className="fs1 cb">
                              {inventory?.inventoryName ||
                                inventory?.inventoryId ||
                                "Main inventory"}
                            </span>
                          </div>

                        </div>

                        <strong className="fs4">
                          {number(
                            inventory?.quantity,
                          )}
                        </strong>

                      </div>
                    ),
                  )
                ) : (
                  <p className="fs2 cb">
                    No inventory locations found.
                  </p>
                )}

              </div>

              <div className="inventoryDrawerActions fx-cl space1">

                <button
                  className="inventoryPrimaryBtn fx-ac fx-jc space"
                  onClick={() => handleSubNavigator("recieves", "Recieves", "Purchases")}
                >
                  <Add />
                  Add stock
                </button>

                <button
                  className="inventorySecondaryBtn fx-ac fx-jc space"
                  onClick={() => handleSubNavigator("adjusment", "Adjustment", "Manage Stock")}
                >
                  <RemoveCircleOutline />
                  Remove stock
                </button>

                <button
                  className="inventorySecondaryBtn fx-ac fx-jc space"
                  onClick={() =>
                    handleSubNavigator("edit-item-display", "Edit Item", "Manage Items", activeProduct)
                  }
                >
                  <EditOutlined />
                  Edit Item
                </button>

                <button
                  className="inventorySecondaryBtn fx-ac fx-jc space"
                  onClick={() =>
                    setAlert({ type: "info", message: "Movement history for this item is not connected yet." })
                  }
                >
                  <History />
                  View movement history
                </button>

              </div>

            </div>

          </aside>
        </div>
      )}

      {/* =========================================================
          STOCK ADJUSTMENT MODAL
      ========================================================== */}

      {showStockModal && activeProduct && (
        <div
          className="inventoryModalOverlay"
          onClick={() =>
            setShowStockModal(false)
          }
        >

          <div
            className="inventoryStockModal fx-cl space2"
            onClick={(event) =>
              event.stopPropagation()
            }
          >

            <div className="fx-jb fx-ac">

              <div className="fx-cl spacem">
                <span className="fs1 cb">
                  INVENTORY OPERATION
                </span>

                <h2 className="fs5 fw600">
                  {stockAction === "add"
                    ? "Add stock"
                    : stockAction === "remove"
                    ? "Remove stock"
                    : "Adjust stock"}
                </h2>
              </div>

              <button
                className="inventoryIconBtn"
                onClick={() =>
                  setShowStockModal(false)
                }
              >
                <Close />
              </button>

            </div>

            <div className="inventoryStockProduct fx-ac space1">

              {activeProduct.__image ? (
                <img
                  src={activeProduct.__image}
                  alt=""
                  className="inventorySmallProductImage"
                />
              ) : (
                <figure className="inventorySmallProductImage inventoryImagePlaceholder fx-ac fx-jc">
                  <Inventory2Outlined />
                </figure>
              )}

              <div className="fx-cl spacem">
                <strong className="fs3">
                  {activeProduct?.name}
                </strong>

                <span className="fs1 cb">
                  Current stock:{" "}
                  {number(activeProduct.__quantity)}
                </span>
              </div>

            </div>

            <div className="inventoryStockActionTabs fx-ac">

              <button
                className={
                  stockAction === "add"
                    ? "inventoryStockActionActive"
                    : ""
                }
                onClick={() =>
                  setStockAction("add")
                }
              >
                <Add />
                Add
              </button>

              <button
                className={
                  stockAction === "remove"
                    ? "inventoryStockActionActive"
                    : ""
                }
                onClick={() =>
                  setStockAction("remove")
                }
              >
                <RemoveCircleOutline />
                Remove
              </button>

              <button
                className={
                  stockAction === "adjust"
                    ? "inventoryStockActionActive"
                    : ""
                }
                onClick={() =>
                  setStockAction("adjust")
                }
              >
                <Assessment />
                Adjust
              </button>

            </div>

            <label className="fx-cl space">
              <span className="fs2 fw500">
                Quantity
              </span>

              <input
                type="number"
                min="1"
                value={stockQuantity}
                onChange={(event) =>
                  setStockQuantity(
                    event.target.value,
                  )
                }
                placeholder="Enter quantity..."
              />
            </label>

            <label className="fx-cl space">
              <span className="fs2 fw500">
                Reason / description
              </span>

              <textarea
                rows="4"
                value={stockReason}
                onChange={(event) =>
                  setStockReason(
                    event.target.value,
                  )
                }
                placeholder="Why is this inventory being changed?"
              />
            </label>

            <div className="inventoryModalFooter fx-je space1">

              <button
                className="inventorySecondaryBtn"
                onClick={() =>
                  setShowStockModal(false)
                }
              >
                Cancel
              </button>

              <button
                className="inventoryPrimaryBtn"
                onClick={executeStockAction}
              >
                Confirm operation
              </button>

            </div>

          </div>
        </div>
      )}
    </>
  );
}